/*
 * Copyright 2024 GLA UK Research and Development Directive
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { from, Observable } from 'rxjs';
import { map, mergeAll, reduce } from 'rxjs/operators';
import { LanguageService } from './language.service';


/**
 * The Translation Loader Service.
 */
@Injectable({
    providedIn: 'root'
})
export class TranslationLoaderService implements TranslateLoader {

    /**
     * Define all the dictionaries we are interested in.
     */
    private dictionaries: string[] = [
        'mail',
        'message',
        'pdf',
        'template',
        'web'
    ];

    /**
     * The Translation Loader Service Constructor.
     */
    constructor(private http: HttpClient,
                private languageService: LanguageService) {
        
    }


    /**
     * Retrieves all the dictionary translations for a specific language.
     * 
     * @param lang          The language to retrieve the translation for
     */
    getTranslation(lang: string): Observable<any> {
        return from(this.dictionaries).pipe(
            map(dict => this.getDictionary(lang, dict)),
            mergeAll(),
            reduce((acc,val) => Object.assign({},acc,val))
        );
    }

    /**
     * Returns the RXJS observable response for a specific dictionary. Note that
     * the response contains all languages but we only map the one requested.
     * 
     * @param lang          The language to retrieve the dictionary for
     * @param dictName      The name of the dictionary to be retrieved
     */
    private getDictionary(lang: string, dictName: string): Observable<any> {
        return this.http.get(`/rest/dictionaries/dictionary/${dictName}/entries`).pipe(
            map((response: any) => {
                const json = {};
                response.forEach((entry: { key: string | number; descs: any[]; }) => entry.descs
                    .filter((desc: { lang: string; value: any; }) => desc.lang === lang)
                    .forEach((desc: { lang: string; value: any; }) => json[entry.key] = desc.value)
                );
                return json;
            })
        ); 
    }
}
